import { z } from "zod/v4";
import type { QueryRowType, Row, Transaction } from "@rocicorp/zero";
import type { Db, DbTransaction } from "../../drizzle";
import { createId } from "@corpuscomplete/core/utils";
import { and, eq, exists, not, type SQLWrapper } from "drizzle-orm";
import { QueryBuilder } from "drizzle-orm/pg-core";
import { nowTimestamp } from "../../utils/sql";
import { createClosureDagClient } from "../../common/closure";
import { foodCategory, foodCategoryClosure, foodCategoryEdge } from "./food-category.sql";
import { foodCategoryAssociationTypes, foodCategoryKeys, type FoodCategoryKey } from "./constants";
import { foodCategoryAssociation } from "../food/food.sql";
import { Schema, zql } from "../../zero/schema";
import { defineQuery } from "../../zero/transformer";
import {
  resolveUsdaBrandedCategoryKeys as resolveUsdaBrandedCategoryKeysInternal,
  resolveUsdaBrandedFoodCategoryKeys as resolveUsdaBrandedFoodCategoryKeysInternal,
  resolveUsdaSrFoundationCategoryKeys as resolveUsdaSrFoundationCategoryKeysInternal,
  resolveUsdaSurveyCategoryKeys as resolveUsdaSurveyCategoryKeysInternal,
} from "./usda-category-keys";

// type DbLike = Db | DbTransaction;

// async function withTransaction<R>(db: DbLike, run: (tx: DbTransaction) => Promise<R>): Promise<R> {
//   if ("zeroDb" in db) {
//     return db.transaction(run);
//   }
//   return run(db);
// }

// function serverDb(tx: Transaction): DbTransaction {
//   if (tx.location !== "server") {
//     throw new Error("FoodCategory mutations require a server transaction");
//   }
//   return tx.dbTransaction.wrappedTransaction;
// }

export namespace FoodCategory {
  export const keys = foodCategoryKeys;
  export type Key = FoodCategoryKey;
  export const commonKeys = [
    keys.fruit,
    keys.vegetable,
    keys.poultry,
    keys.seafood,
    keys.egg,
    keys.dairy,
    keys.grain,
    keys.legume,
    keys.nutSeed,
    keys.herbSpice,
  ] as const satisfies readonly Key[];

  export const all = defineQuery(z.undefined(), () =>
    zql.foodCategory.where("timeArchived", "IS", null),
  );
  export type Info = QueryRowType<typeof all>;

  export async function resolveUsdaBrandedCategoryKeys(
    ...args: Parameters<typeof resolveUsdaBrandedCategoryKeysInternal>
  ) {
    return resolveUsdaBrandedCategoryKeysInternal(...args);
  }

  export async function resolveUsdaBrandedFoodCategoryKeys(
    ...args: Parameters<typeof resolveUsdaBrandedFoodCategoryKeysInternal>
  ) {
    return resolveUsdaBrandedFoodCategoryKeysInternal(...args);
  }

  export async function resolveUsdaSrFoundationCategoryKeys(
    ...args: Parameters<typeof resolveUsdaSrFoundationCategoryKeysInternal>
  ) {
    return resolveUsdaSrFoundationCategoryKeysInternal(...args);
  }

  export async function resolveUsdaSurveyCategoryKeys(
    ...args: Parameters<typeof resolveUsdaSurveyCategoryKeysInternal>
  ) {
    return resolveUsdaSurveyCategoryKeysInternal(...args);
  }

  export const closureDagClient = createClosureDagClient({
    nodeTable: foodCategory,
    edgeTable: foodCategoryEdge,
    closureTable: foodCategoryClosure,
  });

  const filterIdsSchema = z.object({
    includeIds: z.array(z.uuid()).optional(),
    excludeIds: z.array(z.uuid()).optional(),
  });
  export const associationTypeSchema = z.enum(foodCategoryAssociationTypes);
  export type AssociationType = z.infer<typeof associationTypeSchema>;

  export const filterSchema = z.object({
    isA: filterIdsSchema.optional(),
    contains: filterIdsSchema.optional(),
  });
  export type Filter = z.infer<typeof filterSchema>;

  export const createSchema = z.object({
    name: z.string().min(1).max(255),
    key: z.string().min(1).max(255),
    sourceCategoryIds: z.array(z.uuid()),
  });

  const qb = new QueryBuilder();

  async function getPreparedCategoryId(tx: Transaction): Promise<string | null> {
    const preparedCategory = await FoodCategory.getByKey(tx, foodCategoryKeys.prepared);
    return preparedCategory?.id ?? null;
  }

  function normalizeFilterIds(ids?: string[]) {
    const uniqueIds = [...new Set(ids ?? [])];
    return uniqueIds.length > 0 ? uniqueIds : undefined;
  }

  function associationFilterCondition(args: {
    associationType: AssociationType;
    requestedCategoryIds?: string[];
    foodId: SQLWrapper;
    foodVersion: SQLWrapper;
  }) {
    const requestedCategoryIds = normalizeFilterIds(args.requestedCategoryIds);
    if (!requestedCategoryIds) {
      return undefined;
    }

    const matchingAncestor = closureDagClient.descendantHasAnyAncestorCondition({
      descendantId: foodCategoryAssociation.categoryId,
      ancestorIds: requestedCategoryIds,
    });
    if (!matchingAncestor) {
      return undefined;
    }

    return exists(
      qb
        .select({ categoryId: foodCategoryAssociation.categoryId })
        .from(foodCategoryAssociation)
        .where(
          and(
            eq(foodCategoryAssociation.foodId, args.foodId),
            eq(foodCategoryAssociation.foodVersion, args.foodVersion),
            eq(foodCategoryAssociation.type, args.associationType),
            matchingAncestor,
          ),
        ),
    );
  }

  export function filterConditions(args: {
    filter?: Filter;
    foodId: SQLWrapper;
    foodVersion: SQLWrapper;
  }) {
    const conditions = [];

    const isAInclude = associationFilterCondition({
      associationType: "isA",
      requestedCategoryIds: args.filter?.isA?.includeIds,
      foodId: args.foodId,
      foodVersion: args.foodVersion,
    });
    if (isAInclude) {
      conditions.push(isAInclude);
    }

    const isAExclude = associationFilterCondition({
      associationType: "isA",
      requestedCategoryIds: args.filter?.isA?.excludeIds,
      foodId: args.foodId,
      foodVersion: args.foodVersion,
    });
    if (isAExclude) {
      conditions.push(not(isAExclude));
    }

    const containsInclude = associationFilterCondition({
      associationType: "contains",
      requestedCategoryIds: args.filter?.contains?.includeIds,
      foodId: args.foodId,
      foodVersion: args.foodVersion,
    });
    if (containsInclude) {
      conditions.push(containsInclude);
    }

    const containsExclude = associationFilterCondition({
      associationType: "contains",
      requestedCategoryIds: args.filter?.contains?.excludeIds,
      foodId: args.foodId,
      foodVersion: args.foodVersion,
    });
    if (containsExclude) {
      conditions.push(not(containsExclude));
    }

    return conditions;
  }

  // export function manualIsACategoryIds(
  //   associations:
  //     | readonly {
  //         categoryId: string;
  //         type: string;
  //       }[]
  //     | undefined,
  // ): string[] {
  //   if (!associations) {
  //     return [];
  //   }

  //   return Array.from(
  //     new Set(
  //       associations
  //         .filter((association) => association.type === "isA")
  //         .map((association) => association.categoryId),
  //     ),
  //   );
  // }

  export async function getByKey(tx: Transaction, key: string) {
    return tx.run(zql.foodCategory.where("key", "=", key).one());
  }

  export function resolveCategoryIdsFromKeys(args: {
    categoryKeys: readonly string[];
    categoryIdsByKey: ReadonlyMap<string, string>;
  }): string[] {
    const categoryIds: string[] = [];
    const seen = new Set<string>();
    const missingKeys: string[] = [];

    for (const key of args.categoryKeys) {
      const id = args.categoryIdsByKey.get(key);
      if (!id) {
        missingKeys.push(key);
      } else if (!seen.has(id)) {
        categoryIds.push(id);
        seen.add(id);
      }
    }

    if (missingKeys.length > 0) {
      throw new Error(
        `Unknown food category key(s): ${[...new Set(missingKeys)].sort().join(", ")}`,
      );
    }

    return categoryIds;
  }

  export async function resolveCategoryIdsForKeys(
    tx: Transaction,
    categoryKeys: readonly string[],
  ): Promise<string[]> {
    if (categoryKeys.length === 0) {
      return [];
    }

    const categoryRows = await tx.run(
      zql.foodCategory.where("key", "IN", [...new Set(categoryKeys)]),
    );
    return resolveCategoryIdsFromKeys({
      categoryKeys,
      categoryIdsByKey: new Map(categoryRows.map((row) => [row.key, row.id])),
    });
  }

  export async function create(db: Pick<Db, "transaction">, args: z.infer<typeof createSchema>) {
    const parsed = createSchema.parse(args);
    const sourceCategoryIds = Array.from(new Set(parsed.sourceCategoryIds));
    return db.transaction(async (tx) => {
      const timeNow = nowTimestamp();
      const rows = await tx
        .insert(foodCategory)
        .values({
          id: createId(),
          name: parsed.name,
          key: parsed.key,
          timeCreated: timeNow,
          timeUpdated: timeNow,
          timeArchived: null,
        })
        .returning();
      const created = rows[0];
      if (!created) {
        throw new Error(`Failed to create food category. key: "${parsed.key}"`);
      }

      await closureDagClient.registerNode(tx, { nodeId: created.id });
      await Promise.all(
        sourceCategoryIds.map((sourceCategoryId) =>
          closureDagClient.addEdge(tx, {
            sourceId: sourceCategoryId,
            destinationId: created.id,
          }),
        ),
      );

      return created;
    });
  }

  type AddAssociationsArgs = {
    foodId: string;
    foodVersion: number;
    isACategoryIds: string[];
    ingredientFoods?: {
      foodId: string;
      foodVersion: number;
    }[];
  };

  // TODO: Maybe move this to the Food module?
  export async function addAssociations(
    tx: Transaction,
    { foodId, foodVersion, isACategoryIds, ingredientFoods = [] }: AddAssociationsArgs,
  ) {
    const uniqueIsACategoryIds = Array.from(new Set(isACategoryIds));
    const existingAssociations = await tx.run(
      zql.foodCategoryAssociation
        .where("foodId", "=", foodId)
        .where("foodVersion", "=", foodVersion),
    );
    const existingAssociationsByCategoryId = new Map(
      existingAssociations.map((association) => [association.categoryId, association.type]),
    );

    await Promise.all(
      existingAssociations
        .filter(
          (association) =>
            association.type === "contains" &&
            uniqueIsACategoryIds.includes(association.categoryId),
        )
        .map((association) =>
          tx.mutate.foodCategoryAssociation.delete({
            foodId: association.foodId,
            foodVersion: association.foodVersion,
            categoryId: association.categoryId,
          }),
        ),
    );

    await Promise.all(
      uniqueIsACategoryIds
        .filter((categoryId) => existingAssociationsByCategoryId.get(categoryId) !== "isA")
        .map((categoryId) =>
          tx.mutate.foodCategoryAssociation.insert({
            foodId,
            foodVersion,
            categoryId,
            type: "isA",
          }),
        ),
    );

    if (tx.location !== "server") {
      return;
    }

    const preparedCategoryId = await getPreparedCategoryId(tx);
    const allIngredientAssociations = await Promise.all(
      ingredientFoods.map((ingredientFood) =>
        tx.run(
          zql.foodCategoryAssociation
            .where("foodId", "=", ingredientFood.foodId)
            .where("foodVersion", "=", ingredientFood.foodVersion),
        ),
      ),
    );

    const keepContainsAssociationIds = new Set<string>();
    for (const ingredientAssociations of allIngredientAssociations) {
      for (const association of ingredientAssociations) {
        const shouldKeep = await shouldKeepAsContainsAssociation(
          tx.dbTransaction.wrappedTransaction,
          preparedCategoryId,
          association,
        );
        if (shouldKeep) {
          keepContainsAssociationIds.add(association.categoryId);
        }
      }
    }

    await Promise.all(
      Array.from(keepContainsAssociationIds)
        .filter((categoryId) => !uniqueIsACategoryIds.includes(categoryId))
        .filter((categoryId) => !existingAssociationsByCategoryId.has(categoryId))
        .map((categoryId) =>
          tx.mutate.foodCategoryAssociation.insert({
            foodId,
            foodVersion,
            categoryId,
            type: "contains",
          }),
        ),
    );
  }

  async function shouldKeepAsContainsAssociation(
    tx: DbTransaction,
    preparedCategoryId: string | null,
    ingredientAssociation: Row<Schema["tables"]["foodCategoryAssociation"]>,
  ): Promise<boolean> {
    if (ingredientAssociation.type === "contains") {
      return true;
    }

    if (!preparedCategoryId) {
      return false;
    }

    const isPreparedLike = await closureDagClient.descendantHasAnyAncestor(tx, {
      descendantId: ingredientAssociation.categoryId,
      ancestorIds: [preparedCategoryId],
    });
    return !isPreparedLike;
  }
}
